import {
  DeclarativeMapImplementation,
  ElementIdentifier,
  LayerDefinition,
  MAP_PANE_ORDER,
  MapPane,
  MarkerDefinition,
  PaintArrow,
  PaintArrowDefinition,
  PolylineDefinition,
  RotationSource,
} from '@cue/map/core';
import 'leaflet';
import { ensureCanvasMarkerPlugin } from './canvas-marker.plugin';
import { inject, NgZone } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';

declare const L: any;

ensureCanvasMarkerPlugin();

export class LeafletDeclarativeMapImplementation implements DeclarativeMapImplementation, PaintArrow {
  #currentLocationClicked = new Subject<void>();
  currentLocationClicked = this.#currentLocationClicked.asObservable();
  #mapReady = new BehaviorSubject<unknown>(null);
  #layerClicked = new Subject<ElementIdentifier>();
  #layerAppended = new Subject<ElementIdentifier>();
  layerAppended = this.#layerAppended.asObservable();
  #layerRemoved = new Subject<ElementIdentifier>();
  layerRemoved = this.#layerRemoved.asObservable();
  layerClicked = this.#layerClicked.asObservable();

  #markerAppended = new Subject<ElementIdentifier>();
  markerAppended = this.#markerAppended.asObservable();
  #markerRemoved = new Subject<ElementIdentifier>();
  markerRemoved = this.#markerRemoved.asObservable();
  #markerClicked = new Subject<ElementIdentifier>();
  markerClicked = this.#markerClicked.asObservable();

  #polylineRepainted = new Subject<ElementIdentifier>();
  polylineRepainted = this.#polylineRepainted.asObservable();
  #polylineAppended = new Subject<ElementIdentifier>();
  polylineAppended = this.#polylineAppended.asObservable();
  #polylineRemoved = new Subject<ElementIdentifier>();
  polylineRemoved = this.#polylineRemoved.asObservable();
  #imageAppended = new Subject<ElementIdentifier>();
  imageAppended = this.#imageAppended.asObservable();
  #zoomChanged = new Subject<number>();
  zoomChanged = this.#zoomChanged.asObservable();

  #debug = false;

  enableDebug() {
    this.#debug = true;
  }

  supportsRotation(): boolean {
    return false;
  }

  setRotationEnabled(enabled: boolean): void {}

  setGpsAutoRotateAvailable(available: boolean): void {}

  setRotation(rotation: number, animate?: boolean, source?: RotationSource): void {}

  private setMapCursor(cursor: '' | 'pointer') {
    this.#map?.getContainer().style.setProperty('cursor', cursor);
  }

  onMapReady(action: (map: any) => void) {
    firstValueFrom(this.#mapReady).then(() => action(this.#map));
  }

  focusLayer(identifier: ElementIdentifier) {
    const found = this.#layers.get(identifier);
    if (!found) {
      return;
    }
    if (this.#debug) {
      console.log('Focusing layer', identifier, found.getBounds());
    }
    this.#map?.fitBounds(found.getBounds());
  }

  #currentLocationControl?: any;
  #currentLocationMarker?: any;

  private removeBackToStart() {
    this.#currentLocationMarker?.remove();
    this.#currentLocationMarker = null;
  }

  private paintBackToStart() {
    this.onMapReady(() => {
      if (this.#currentLocationMarker || this.#map == null) {
        return;
      }

      const currentLocationClicked = this.#currentLocationClicked;
      this.#currentLocationControl = L.Control.extend({
        options: {
          position: 'bottomright',
        },
        onAdd: function (map) {
          const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');

          const button = L.DomUtil.create('a', 'leaflet-control-button back-to-start', container);
          button.innerHTML = `
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_679_3151)">
              <path d="M15.3931 10.9998C15.3931 13.4298 13.4232 15.3998 10.9932 15.3998C8.56322 15.3998 6.59346 13.4298 6.59346 10.9998C6.59346 8.56987 8.56322 6.60011 10.9932 6.60011C13.4232 6.60011 15.3931 8.56987 15.3931 10.9998Z" fill="currentColor"/>
              <path d="M21.3401 10.3398H19.869C19.5383 5.94654 16.0467 2.45435 11.6534 2.12323V0.660011C11.6534 0.295464 11.3577 0 10.9934 0C10.6288 0 10.3334 0.295464 10.3334 0.660011V2.12323C5.93989 2.45435 2.4481 5.94654 2.11739 10.3398H0.660011C0.295464 10.3398 0 10.6355 0 10.9998C0 11.3644 0.295464 11.6599 0.660011 11.6599H2.11739C2.4481 16.0531 5.93989 19.5453 10.3332 19.8765V21.3401C10.3332 21.7046 10.6286 22.0001 10.9932 22.0001C11.3575 22.0001 11.653 21.7046 11.653 21.3401V19.8765C16.0465 19.5455 19.5383 16.0533 19.869 11.6599H21.3401C21.7046 11.6599 22.0001 11.3644 22.0001 10.9998C22.0001 10.6355 21.7046 10.3398 21.3401 10.3398ZM10.9932 18.5895C6.80171 18.5893 3.40398 15.1911 3.40398 10.9996C3.40418 6.80796 6.80232 3.41022 10.9938 3.41022C15.1855 3.41022 18.5834 6.80836 18.5834 10.9998C18.5784 15.1897 15.1831 18.5848 10.9932 18.5895Z" fill="currentColor"/>
            </g>
            <defs>
              <clipPath id="clip0_679_3151">
                <rect width="22" height="22" fill="currentColor"/>
              </clipPath>
            </defs>
          </svg>
        `;

          button.onclick = function () {
            currentLocationClicked.next();
          };
          container.title = 'Back To Start';
          return container;
        },
        onRemove: function (map) {},
      });

      this.#currentLocationMarker = new this.#currentLocationControl();
      this.#currentLocationMarker.addTo(this.#map);
    });
  }

  showCurrentLocationButton(show: boolean) {
    if (show) {
      this.paintBackToStart();
    } else {
      this.removeBackToStart();
    }
  }
  invalidateMapSize(): void {
    this.onMapReady((map) => {
      map.invalidateSize();
      const existingImage = this.#imageOverlays.entries().next().value;
      if (existingImage) {
        map.fitBounds(existingImage[1].getBounds());
      }
    });
  }

  focusMarker(identifier: ElementIdentifier, align?: 'top-center') {
    const found = this.#markers.get(identifier);
    if (!found) {
      return;
    }

    if (this.#debug) {
      console.log('Focusing marker', identifier, found.getLatLng());
    }

    if (align === 'top-center') {
      const point = this.#map.latLngToContainerPoint(found.getLatLng());

      // Shift point down (so marker appears at top center)
      const offsetPoint = L.point(point.x, point.y + this.#map.getSize().y / 2 - 50);

      // Convert back to latlng
      const targetLatLng = this.#map.containerPointToLatLng(offsetPoint);

      // Now set the view
      this.#map.setView(targetLatLng, this.#map.getZoom(), { animate: true });
    } else {
      this.#map?.setView(found.getLatLng(), this.#map.getZoom(), {});
    }
  }

  private customMapControls(): void {
    const CustomZoomControl = L.Control.Zoom.extend({
      onAdd: function (map: L.Map) {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-zoom');

        this._zoomInButton = this._createButton(
          `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_2195_108)">
            <path d="M15.8045 14.8628L11.8252 10.8835C12.9096 9.55723 13.4428 7.86489 13.3144 6.15654C13.1861 4.44819 12.406 2.85452 11.1356 1.70518C9.86516 0.555838 8.20158 -0.0612406 6.48895 -0.0184194C4.77632 0.0244018 3.14566 0.723847 1.93426 1.93524C0.72287 3.14663 0.0234252 4.77729 -0.019396 6.48993C-0.0622172 8.20256 0.554862 9.86614 1.7042 11.1366C2.85354 12.407 4.44721 13.187 6.15556 13.3154C7.86392 13.4438 9.55625 12.9106 10.8825 11.8262L14.8619 15.8055C14.9876 15.9269 15.156 15.9941 15.3308 15.9926C15.5056 15.9911 15.6728 15.921 15.7964 15.7974C15.92 15.6738 15.9901 15.5066 15.9916 15.3318C15.9932 15.157 15.926 14.9886 15.8045 14.8628ZM6.66652 12.0008C5.61169 12.0008 4.58054 11.688 3.70348 11.102C2.82642 10.516 2.14283 9.68302 1.73916 8.70848C1.3355 7.73394 1.22988 6.66158 1.43567 5.62702C1.64145 4.59245 2.14941 3.64214 2.89529 2.89626C3.64117 2.15038 4.59147 1.64243 5.62604 1.43664C6.66061 1.23086 7.73296 1.33647 8.7075 1.74014C9.68204 2.14381 10.515 2.82739 11.101 3.70446C11.6871 4.58152 11.9999 5.61266 11.9999 6.6675C11.9983 8.0815 11.4359 9.43713 10.436 10.437C9.43615 11.4368 8.08052 11.9992 6.66652 12.0008Z" fill="currentColor"/>
            <path d="M8.66666 6.00036H7.33333V4.66702C7.33333 4.49021 7.26309 4.32064 7.13807 4.19562C7.01304 4.0706 6.84347 4.00036 6.66666 4.00036C6.48985 4.00036 6.32028 4.0706 6.19526 4.19562C6.07024 4.32064 6 4.49021 6 4.66702V6.00036H4.66667C4.48986 6.00036 4.32029 6.07059 4.19526 6.19562C4.07024 6.32064 4 6.49021 4 6.66702C4 6.84383 4.07024 7.0134 4.19526 7.13842C4.32029 7.26345 4.48986 7.33369 4.66667 7.33369H6V8.66702C6 8.84383 6.07024 9.0134 6.19526 9.13842C6.32028 9.26344 6.48985 9.33368 6.66666 9.33368C6.84347 9.33368 7.01304 9.26344 7.13807 9.13842C7.26309 9.0134 7.33333 8.84383 7.33333 8.66702V7.33369H8.66666C8.84347 7.33369 9.01304 7.26345 9.13806 7.13842C9.26309 7.0134 9.33333 6.84383 9.33333 6.66702C9.33333 6.49021 9.26309 6.32064 9.13806 6.19562C9.01304 6.07059 8.84347 6.00036 8.66666 6.00036Z" fill="currentColor"/>
            </g>
            </svg>`,
          'Zoom in',
          'leaflet-control-zoom-in',
          container,
          () => map.zoomIn()
        );

        this._zoomOutButton = this._createButton(
          `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_2195_9043)">
            <path d="M15.8045 14.8628L11.8252 10.8835C12.9096 9.55723 13.4428 7.86489 13.3144 6.15654C13.1861 4.44819 12.406 2.85452 11.1356 1.70518C9.86516 0.555838 8.20158 -0.0612406 6.48895 -0.0184194C4.77632 0.0244018 3.14566 0.723847 1.93426 1.93524C0.72287 3.14663 0.0234252 4.77729 -0.019396 6.48993C-0.0622172 8.20256 0.554862 9.86614 1.7042 11.1366C2.85354 12.407 4.44721 13.187 6.15556 13.3154C7.86392 13.4438 9.55625 12.9106 10.8825 11.8262L14.8619 15.8055C14.9876 15.9269 15.156 15.9941 15.3308 15.9926C15.5056 15.9911 15.6728 15.921 15.7964 15.7974C15.92 15.6738 15.9901 15.5066 15.9916 15.3318C15.9932 15.157 15.926 14.9886 15.8045 14.8628ZM6.66652 12.0008C5.61169 12.0008 4.58054 11.688 3.70348 11.102C2.82642 10.516 2.14283 9.68302 1.73916 8.70848C1.3355 7.73394 1.22988 6.66158 1.43567 5.62702C1.64145 4.59245 2.14941 3.64214 2.89529 2.89626C3.64117 2.15038 4.59147 1.64243 5.62604 1.43664C6.66061 1.23086 7.73296 1.33647 8.7075 1.74014C9.68204 2.14381 10.515 2.82739 11.101 3.70446C11.6871 4.58152 11.9999 5.61266 11.9999 6.6675C11.9983 8.0815 11.4359 9.43713 10.436 10.437C9.43615 11.4368 8.08052 11.9992 6.66652 12.0008Z" fill="currentColor"/>
            <path d="M8.66666 6.00036H4.66667C4.48986 6.00036 4.32029 6.0706 4.19526 6.19562C4.07024 6.32064 4 6.49021 4 6.66702C4 6.84383 4.07024 7.0134 4.19526 7.13843C4.32029 7.26345 4.48986 7.33369 4.66667 7.33369H8.66666C8.84347 7.33369 9.01304 7.26345 9.13806 7.13843C9.26309 7.0134 9.33333 6.84383 9.33333 6.66702C9.33333 6.49021 9.26309 6.32064 9.13806 6.19562C9.01304 6.0706 8.84347 6.00036 8.66666 6.00036Z" fill="currentColor"/>
            </g>
            </svg>`,
          'Zoom out',
          'leaflet-control-zoom-out',
          container,
          () => map.zoomOut()
        );

        map.on('zoomend zoomlevelschange', this._updateDisabled, this);
        this._updateDisabled();

        return container;
      },
      _updateDisabled: function () {
        const { _map: map } = this;
        const classNameDisabled = 'leaflet-disabled';
        if (!map) return;

        const zoomInDisabled = map.getZoom() === map.getMaxZoom();
        const zoomOutDisabled = map.getZoom() === map.getMinZoom();

        this._zoomInButton.classList.toggle(classNameDisabled, zoomInDisabled);
        this._zoomOutButton.classList.toggle(classNameDisabled, zoomOutDisabled);
      },
    });

    const ResetViewControl = L.Control.extend({
      options: { position: 'bottomright' },
      onAdd: (map) => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('a', 'leaflet-control-button reset-view', container);
        const llBounds = map.getBounds();

        button.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <g clip-path="url(#a)" fill="currentColor">
          <path d="M16.663 8.003h5.33a.666.666 0 0 0 0-1.333h-3.721l5.525-5.525a.666.666 0 1 0-.942-.942L17.33 5.728V2.007a.666.666 0 1 0-1.333 0v5.33c0 .367.299.666.666.666ZM7.337 15.997h-5.33a.666.666 0 1 0 0 1.333h3.721L.203 22.855a.666.666 0 1 0 .942.942l5.525-5.525v3.721a.666.666 0 0 0 1.333 0v-5.33a.666.666 0 0 0-.666-.666ZM18.272 17.33h3.721a.666.666 0 0 0 0-1.333h-5.33a.666.666 0 0 0-.666.666v5.33a.666.666 0 1 0 1.333 0v-3.721l5.525 5.525a.666.666 0 0 0 .942-.942l-5.525-5.525ZM7.337 1.34a.666.666 0 0 0-.667.667v3.721L1.145.203a.666.666 0 0 0-.942.942L5.728 6.67H2.007a.666.666 0 1 0 0 1.333h5.33a.666.666 0 0 0 .666-.666v-5.33a.666.666 0 0 0-.666-.666Z"/>
        </g>
        <defs>
          <clipPath id="a">
            <path fill="currentColor" d="M0 0h24v24H0z"/>
          </clipPath>
        </defs>
      </svg>
    `;

        L.DomEvent.disableClickPropagation(button);
        L.DomEvent.on(button, 'click', () => {
          if (this.#imageOverlays.size === 0) {
            return;
          }
          const centerOfBounds = this.#imageOverlays.entries().next().value[1].getBounds();
          this.#map.fitBounds(centerOfBounds);

          // if (this.#defaults?.zoom >= 1) {
          //   this.setZoom(this.#imageOverlays.entries().next().value[0], this.#defaults.zoom);
          // }
        });
        return container;
      },
    });

    const customZoomControl = new CustomZoomControl({ position: 'bottomright' });
    const resetViewControl = new ResetViewControl();

    this.#map.addControl(resetViewControl);
    this.#map.addControl(customZoomControl);
  }

  removeLayer(identifier: ElementIdentifier): void {
    const found = this.#layers.get(identifier);
    if (!found) {
      return;
    }
    this.#map?.removeLayer(found);
    this.#layers.delete(identifier);
  }

  #previousClassName?: string;

  editMarker(identifier: ElementIdentifier, markerDefinitionPatch: Partial<MarkerDefinition>) {
    const foundMarker = this.#markers.get(identifier);
    if (!foundMarker) {
      return;
    }

    if (markerDefinitionPatch.geoJson !== undefined) {
      this.reprojectWhileNotAnimating(() => {
        const latlng = L.geoJSON(markerDefinitionPatch.geoJson).getLayers()[0].getLatLng();
        foundMarker.setLatLng(latlng);
      });
    }

    if (markerDefinitionPatch.element !== undefined) {
      const divIcon = L.divIcon({
        className: markerDefinitionPatch.className ? markerDefinitionPatch : '',
        html: markerDefinitionPatch.element ?? '',
      });
      foundMarker.setIcon(divIcon);
    }

    if (markerDefinitionPatch.className !== undefined) {
      foundMarker.getElement()?.classList.add(markerDefinitionPatch.className);
    }

    if (markerDefinitionPatch.zIndexOffset !== undefined) {
      foundMarker.setZIndexOffset(markerDefinitionPatch.zIndexOffset);
    }
    if (markerDefinitionPatch.className !== undefined) {
      if (this.#previousClassName) {
        foundMarker.getElement()?.classList.remove(this.#previousClassName);
      }
      foundMarker.getElement()?.classList.add(markerDefinitionPatch.className);
    }
  }

  removeMarker(identifier: ElementIdentifier): void {
    const found = this.#markers.get(identifier);
    if (!found) {
      return;
    }
    this.#map?.removeLayer(found);
    this.#markers.delete(identifier);
  }

  editPolyline(identifier: ElementIdentifier, polylineDefinitionPatch: Partial<PolylineDefinition>) {
    const found = this.#polylines.get(identifier);
    if (!found) {
      return;
    }

    if (polylineDefinitionPatch.pointlist !== undefined) {
      this.reprojectWhileNotAnimating(() => {
        const latlngs = polylineDefinitionPatch.pointlist.map((point) => [point.lat, point.lng]);
        found.setLatLngs(latlngs as any);
        // TODO dat synchronizacni mechanismus
        setTimeout(() => {
          this.#polylineRepainted.next(identifier);
        }, 200);
      });
    }

    const options: any = {};

    // COlor
    if (polylineDefinitionPatch.color !== undefined) {
      found.setStyle({ color: polylineDefinitionPatch.color });
    }

    if (polylineDefinitionPatch.className !== undefined) {
      options.className = polylineDefinitionPatch.className;
    }
    if (polylineDefinitionPatch.pane !== undefined) {
      options.pane = this.getPaneName(polylineDefinitionPatch.pane, MapPane.Path);
      options.renderer = this.getPaneRenderer(polylineDefinitionPatch.pane, MapPane.Path);
    }
    found.setStyle(options);
  }

  getPathRenderer() {
    return this.getPaneRenderer(MapPane.Path, MapPane.Path);
  }
  removePolyline(identifier: ElementIdentifier) {
    const found = this.#polylines.get(identifier);
    if (!found) {
      return;
    }
    this.#map?.removeLayer(found);
    this.#polylines.delete(identifier);
  }

  focusPolyline(identifier: ElementIdentifier) {
    const found = this.#polylines.get(identifier);
    if (!found) {
      return;
    }

    if (this.#debug) {
      console.log('Focusing polyline', identifier, found.getBounds());
    }

    if (this.#moving) {
      this.#map.once('moveend', () => {
        this.#map?.fitBounds(found.getBounds(), {
          padding: [30, 30],
        });
      });
    } else if (this.#zooming) {
      this.#map.once('zoomend', () => {
        this.#map?.fitBounds(found.getBounds(), {
          padding: [30, 30],
        });
      });
    } else {
      this.#map?.fitBounds(found.getBounds(), {
        padding: [30, 30],
      });
    }
  }

  addPolyline(identifier: ElementIdentifier, polylineDefinition: PolylineDefinition) {
    this.onMapReady(() => {
      this.reprojectWhileNotAnimating(() => {
        const latlngs = polylineDefinition.pointlist.map((point) => [point.lat, point.lng]);

        const options = {
          color: polylineDefinition.color,
          dashArray: '5,10',
          dashOffset: '0',
          opacity: 1,
          weight: 5,
          className: polylineDefinition.className ?? '',
        };

        (options as any).pane = this.getPaneName(polylineDefinition.pane, MapPane.Path);
        (options as any).renderer = this.getPaneRenderer(polylineDefinition.pane, MapPane.Path);
        const polyline = (L as any).polyline(latlngs, options);

        this.#polylines.set(identifier, polyline);
        polyline.addTo(this.#map!);
        this.#polylineAppended.next(identifier);
      });
    });
  }

  addMarker(identifier: ElementIdentifier, markerDefinition: MarkerDefinition, fitMap?: boolean): void {
    this.onMapReady(() => {
      this.reprojectWhileNotAnimating(() => {
        const latlng = L.geoJSON(markerDefinition.geoJson).getLayers()[0].getLatLng();

        const divIcon = L.divIcon({
          className: markerDefinition.className ? markerDefinition.className : '',
          html: markerDefinition.element ?? '',
        });

        if (markerDefinition.element) {
          L.DomEvent.disableClickPropagation(markerDefinition.element);
          markerDefinition.element.style.setProperty('cursor', 'pointer');
          markerDefinition.element.style.setProperty('touch-action', 'manipulation');
          markerDefinition.element.style.setProperty('-webkit-tap-highlight-color', 'transparent');
        }

        const options: any = {
          icon: divIcon,
          pane: this.getPaneName(markerDefinition.pane, MapPane.Poi),
          zIndexOffset: markerDefinition.zIndexOffset ?? 0,
        };
        const marker = L.marker(latlng, options);

        marker.on('click', () => {
          this.#markerClicked.next(identifier);
        });
        marker.on('mouseover', () => {
          this.setMapCursor('pointer');
        });
        marker.on('mouseout', () => {
          this.setMapCursor('');
        });
        marker.on('add', () => {
          const markerElement = marker.getElement();
          markerElement?.style.setProperty('cursor', 'pointer');
          markerElement?.style.setProperty('touch-action', 'manipulation');
          markerElement?.style.setProperty('-webkit-tap-highlight-color', 'transparent');
        });
        marker.on('remove', () => {
          this.setMapCursor('');
        });

        this.#markers.set(identifier, marker);
        marker.addTo(this.#map!);
        this.#markerAppended.next(identifier);
        this.#map?.invalidateSize();
        if (fitMap) {
          // Pokud mame uz mapku, tak vyuzijeme jejich boundariers
          if (this.#imageOverlays.entries().next().value?.[1]) {
            this.#map?.fitBounds(this.#imageOverlays.entries().next().value[1].getBounds(), {
              animate: false,
            });
          }

          this.#map?.setView(latlng, this.#map.getZoom());
        }
      });
    });
  }

  addLayer(identifier: ElementIdentifier, polygonDefinition: LayerDefinition, radius?: number, fitMap?: boolean) {
    this.onMapReady(() => {
      const isInteractive = polygonDefinition.interactive ?? false;
      // this.reprojectWhileNotAnimating(() => {
      const options: any = {
        interactive: isInteractive,
        pane: this.getPaneName(polygonDefinition.pane, MapPane.Resource),
        renderer: this.getPaneRenderer(polygonDefinition.pane, MapPane.Resource),
        style: {
          color: polygonDefinition.color,
          fill: polygonDefinition.fill,
          fillOpacity: polygonDefinition.fillOpacity,
          fillColor: polygonDefinition.fillColor,
          weight: polygonDefinition.weight,
        },
        pointToLayer: (feature, latlng, x) => {
          if (radius) {
            let options: any = {};
            options.interactive = isInteractive;
            options.pane = this.getPaneName(polygonDefinition.pane, MapPane.Resource);
            options.renderer = this.getPaneRenderer(polygonDefinition.pane, MapPane.Resource);
            return new L.Circle(latlng, radius, options);
          } else {
            return new L.Marker(latlng, {
              pane: this.getPaneName(polygonDefinition.pane, MapPane.Resource),
            });
          }
        },
      };

      const polygon = L.geoJSON(polygonDefinition.geoJson, options);

      if (isInteractive) {
        polygon.on('click', () => {
          this.#layerClicked.next(identifier);
        });
        polygon.on('mouseover', () => {
          this.setMapCursor('pointer');
        });
        polygon.on('mouseout', () => {
          this.setMapCursor('');
        });
      }
      polygon.on('remove', () => {
        this.setMapCursor('');
      });

      polygon.addTo(this.#map!);
      this.#layerAppended.next(identifier);
      if (fitMap) {
        this.#map?.fitBounds(polygon.getBounds(), {
          animate: false,
        });
      }

      this.#layers.set(identifier, polygon);
      // });
    });
  }

  scheduleMapAvailable(action: () => void) {
    if (this.#map) {
      action();
    } else {
      const interval = setInterval(() => {
        if (this.#map) {
          clearInterval(interval);
          action();
        }
      }, 10);
    }
  }

  private reprojectWhileNotAnimating(action: () => void) {
    if (!this.#moving) {
      action();
    } else {
      this.#map.once('moveend', () => {
        action();
      });
    }
  }

  editLayer(identifier: ElementIdentifier, polygonDefinitionPatch: Partial<LayerDefinition>, radius?: number): void {
    const foundPolygon = this.#layers.get(identifier);
    if (!foundPolygon) {
      return;
    }

    if (polygonDefinitionPatch.geoJson !== undefined) {
      this.reprojectWhileNotAnimating(() => {
        if (radius) {
          // polygon is circle

          (foundPolygon as any).eachLayer((layer) => {
            if (layer instanceof L.Circle) {
              layer.setLatLng([
                (polygonDefinitionPatch.geoJson.coordinates as any)[1],
                (polygonDefinitionPatch.geoJson.coordinates as any)[0],
              ]);
            }
          });
        } else {
          const latlngs = polygonDefinitionPatch.geoJson.coordinates[0].map((coord) => {
            return [coord[1], coord[0]];
          });
          foundPolygon.setLatLngs(latlngs);
        }
      });
    }

    if (polygonDefinitionPatch.color !== undefined) {
      foundPolygon.setStyle({ color: polygonDefinitionPatch.color });
    }
    if (polygonDefinitionPatch.fill !== undefined) {
      foundPolygon.setStyle({ fill: polygonDefinitionPatch.fill });
    }
    if (polygonDefinitionPatch.fillOpacity !== undefined) {
      foundPolygon.setStyle({ fillOpacity: polygonDefinitionPatch.fillOpacity });
    }
    if (polygonDefinitionPatch.fillColor !== undefined) {
      foundPolygon.setStyle({ fillColor: polygonDefinitionPatch.fillColor });
    }
    if (polygonDefinitionPatch.weight !== undefined) {
      foundPolygon.setStyle({ weight: polygonDefinitionPatch.weight });
    }
  }
  #map?: L.Map;
  #imageOverlays: Map<ElementIdentifier, L.ImageOverlay> = new Map();
  #layers: Map<ElementIdentifier, L.Polygon> = new Map();
  #markers: Map<ElementIdentifier, L.Marker> = new Map();
  #polylines: Map<ElementIdentifier, L.Polyline> = new Map();
  #paintedArrow?: any;
  #paintedArrowPolyline?: L.Polyline;

  #zone = inject(NgZone);

  destroy() {
    this.clearArrow();
    if (this.#map) {
      this.#map.remove();
      this.#map = undefined;
    }
    this.#imageOverlays.clear();
    this.#paneRenderers.clear();

    this.#boundResizeObserver?.disconnect();
    this.#boundResizeObserver = undefined;
  }

  paintArrow(definition: PaintArrowDefinition): void {
    if (!definition.pointlist.length) {
      this.clearArrow();
      return;
    }

    this.onMapReady((map) => {
      this.clearArrow();

      this.#paintedArrowPolyline = L.polyline(
        definition.pointlist.map((point) => L.latLng([point.lat, point.lng])),
        {
          noClip: true,
          color: definition.color,
          weight: 5,
          pane: this.getPaneName(MapPane.Path, MapPane.Path),
          renderer: this.getPathRenderer(),
        }
      );
      this.#paintedArrowPolyline.addTo(map);

      this.#paintedArrow = (L as any).canvasMarker(L.latLng([definition.end.lat, definition.end.lng]), {
        pane: this.getPaneName(MapPane.Path, MapPane.Path),
        forcePane: true,
        prevLatlng: L.latLng([definition.start.lat, definition.start.lng]),
        img: {
          url: './assets/images/arrow-new.svg',
          size: [50, 50],
          rotate: 0,
        },
      });
      this.#paintedArrow.addTo(map);
    });
  }

  clearArrow(): void {
    this.#paintedArrow?.remove();
    this.#paintedArrow = undefined;
    this.#paintedArrowPolyline?.remove();
    this.#paintedArrowPolyline = undefined;
  }

  #zooming = false;
  #moving = false;
  #paneRenderers = new Map<MapPane, any>();
  #defaults?: { zoom: number; minZoom: number; maxZoom: number };
  #boundResizeObserver?: ResizeObserver;
  initializeCanvas(
    identifier: ElementIdentifier,
    nativeElement: HTMLElement,
    defaults: { zoom: number; minZoom: number; maxZoom: number }
  ): void {
    this.#defaults = defaults;
    this.#zone.runOutsideAngular(() => {
      this.#map = L.map(nativeElement, {
        preferCanvas: true,
        center: [0, 0],
        attributionControl: false,
        zoom: defaults.zoom,
        minZoom: defaults.minZoom,
        zoomSnap: 0,
        zoomDelta: 0.25,
        maxZoom: defaults.maxZoom,
        crs: L.CRS.Simple,
        wheelDebounceTime: 1,
        zoomControl: false,
      });
    });

    this.#boundResizeObserver = new ResizeObserver(() => {
      this.#map?.invalidateSize();
    });

    this.#boundResizeObserver.observe(nativeElement);
    this.customMapControls();

    this.#map?.whenReady(() => {
      this.#mapReady.next({});
    });

    this.#zone.runOutsideAngular(() => {
      this.#map.on('movestart', () => {
        this.#moving = true;
        this.#map.once('moveend', () => {
          this.#moving = false;
        });
      });
      this.#map.on('zoom', () => {
        this.#zoomChanged.next(this.#map!.getZoom());
        this.#zooming = true;
        this.#map.once('zoomend', () => {
          this.#zooming = false;
        });
      });

      this.initializeMapPanes();
    });
  }

  private getPaneName(pane: MapPane | null | undefined, fallback: MapPane): string {
    return `cue-map-${pane ?? fallback}`;
  }

  private getPaneRenderer(pane: MapPane | null | undefined, fallback: MapPane): any {
    const resolvedPane = pane ?? fallback;
    let renderer = this.#paneRenderers.get(resolvedPane);
    if (!renderer) {
      renderer = L.svg({ pane: this.getPaneName(resolvedPane, fallback) });
      this.#paneRenderers.set(resolvedPane, renderer);
    }
    return renderer;
  }

  private initializeMapPanes(): void {
    if (!this.#map) {
      return;
    }
    for (const pane of Object.values(MapPane)) {
      const paneElement = this.#map.createPane(this.getPaneName(pane, pane));
      paneElement.style.zIndex = String(100 + MAP_PANE_ORDER[pane] * 100);
    }
  }

  addImage(identifier: ElementIdentifier, src: string, width: number, height: number, fitMap: boolean): void {
    this.scheduleMapAvailable(() => {
      const southWest = this.#map.unproject([0, height], 2);
      const northEast = this.#map.unproject([width, 0], 2);
      const bounds = new L.LatLngBounds(southWest, northEast);
      const overlay = L.imageOverlay(src, bounds, {
        pane: this.getPaneName(MapPane.Map, MapPane.Map),
        zIndex: 0,
      }).addTo(this.#map);
      this.#imageOverlays.set(identifier, overlay);
      if (fitMap) {
        this.#map.fitBounds(bounds);
      }
    });
  }

  focusImage(identifier: ElementIdentifier) {
    const foundOverlay = this.#imageOverlays.get(identifier);
    if (!foundOverlay) {
      return;
    }

    if (this.#debug) {
      console.log('Focusing image', identifier, foundOverlay.getBounds());
    }

    if (this.#moving) {
      this.#map.once('moveend', () => {
        this.#map?.fitBounds(foundOverlay.getBounds());
      });
    } else if (this.#zooming) {
      this.#map.once('zoomend', () => {
        this.#map?.fitBounds(foundOverlay.getBounds());
      });
    } else {
      this.#map?.fitBounds(foundOverlay.getBounds());
    }
  }

  setImageSrc(identifier: ElementIdentifier, src: string, width: number, height: number, fitMap: boolean): void {
    const foundOverlay = this.#imageOverlays.get(identifier);
    if (!foundOverlay) {
      return;
    }
    const southWest = this.#map.unproject([0, height], 2);
    const northEast = this.#map.unproject([width, 0], 2);
    const bounds = new L.LatLngBounds(southWest, northEast);
    foundOverlay.setUrl(src);
    foundOverlay.setBounds(bounds);
    if (fitMap) {
      setTimeout(() => {
        if (this.#moving) {
          this.#map.once('moveend', () => {
            if (this.#debug) {
              console.log('Fitting MOVEVEND map after moveend');
            }
            this.#map.fitBounds(bounds);
          });
          return;
        } else if (this.#zooming) {
          this.#map.once('zoomend', () => {
            if (this.#debug) {
              console.log('Fitting ZOOMEND map after zoomend');
            }
            this.#map.fitBounds(bounds);
          });
          return;
        } else {
          if (this.#debug) {
            console.log('Fitting NORMAL map');
          }
          this.#map.fitBounds(bounds);
        }
      }, 1);
    }
  }

  setResetZoom(identifier: ElementIdentifier, number: number): void {
    if (this.#defaults) {
      this.#defaults.zoom = number;
    }
  }

  setZoom(identifier: ElementIdentifier, number: number): void {
    if (this.#map) {
      if (this.#debug) {
        console.log('Setting zoom to', number);
      }
      this.#map.setZoom(number);
    }
  }
  setMinZoom(identifier: ElementIdentifier, number: number): void {
    if (this.#debug) {
      console.log('Set min zoom', identifier, number);
    }
    if (this.#defaults) {
      this.#defaults.minZoom = number;
    }
    if (this.#map) {
      this.#map.setMinZoom(number);
    }
  }
  setMaxZoom(identifier: ElementIdentifier, number: number): void {
    if (this.#debug) {
      console.log('Set max zoom', identifier, number);
    }
    if (this.#defaults) {
      this.#defaults.maxZoom = number;
    }
    if (this.#map) {
      this.#map.setMaxZoom(number);
    }
  }
}
